import AbstractFetch from '~/repositories/AbstractFetch';
import type { IFetchOptions, IResponse } from '~/types/repositories';
import type { IOrderLine, IOrderParams } from '~/modules/checkout/types/orders';
import type { ICreateInvoiceResponse, IOrder, TOrderListResponse, TOrderInvoiceResponse } from '~/modules/user/types/order';

type THeadersWithoutContentType = Record<string, string> & { 'Content-Type'?: never };

type TJsonBody = {
  type: string;
  data: Record<string, string>;
};

type IPostFormPayload
  = | {
    body: FormData;
    headers?: THeadersWithoutContentType;
  }
  | {
    body: TJsonBody;
    headers?: Record<string, string>;
  };

export default class Orders extends AbstractFetch {
  private static readonly ORDER_BASE = 'v1/orders/';
  private static readonly ORDER_INIT = 'v1/orders/init/';

  constructor(fetchOptions: IFetchOptions) {
    super(fetchOptions.baseURL, fetchOptions.fetch);
  }

  async initOrder(params: IOrderParams, signal?: AbortSignal): Promise<any> {
    return this.fetch<IResponse<void>>(Orders.ORDER_INIT, {
      method: 'POST',
      body: params,
      signal,
    });
  }

  async createOrder(params: IOrderParams): Promise<any> {
    return this.fetch<IResponse<void>>(Orders.ORDER_BASE, {
      method: 'POST',
      body: params,
    });
  }

  async getOrder(order_id: string) {
    const url = Orders.ORDER_BASE + order_id;
    return this.fetch<IResponse<IOrder>>(url, {
      method: 'GET',
    });
  }

  async cancelOrder(id: string): Promise<IResponse<void>> {
    const url = Orders.ORDER_BASE + id + '/cancel';
    return this.fetch(url, {
      method: 'POST',
    });
  }

  async createInvoice(order_id: string) {
    const url = Orders.ORDER_BASE + order_id + '/invoices/';

    return this.fetch<IResponse<ICreateInvoiceResponse>>(url, {
      method: 'PUT',
    });
  }

  async returnOrder(order_id: string, payload: IPostFormPayload): Promise<TOrderListResponse> {
    const url = Orders.ORDER_BASE + order_id + '/return';

    return this.fetch(`${url}`, {
      method: 'POST',
      ...payload,
    });
  }

  async getOrderInvoices(id: string): Promise<TOrderInvoiceResponse> {
    const url = Orders.ORDER_BASE + id + '/invoices';
    return this.fetch(url, {
      method: 'PUT',
    });
  }
}
